import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../shared/utils/api';

/* ── Ask Alex micro-tutor ────────────────────────────────────────────────── */
const MAX_ALEX_EXCHANGES = 5;

const ALEX_LIMIT_MESSAGE = "I think we're going in circles on this one. Do a few more questions on the topic, then come back to me if you're still stuck. Sometimes seeing it applied to different facts helps more than me repeating the same explanation.";

function stripMarkdown(text) {
  return text
    .replace(/\*{1,2}([^*]+)\*{1,2}/g, '$1')   // **bold** and *italic*
    .replace(/^#{1,6}\s+/gm, '')                 // # headers
    .replace(/^[\s-]*-\s+/gm, '')                // - list items
    .trim();
}

function AskAlex({ questionId, wasCorrect }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role:'user'|'alex', text}]
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [exchangeCount, setExchangeCount] = useState(0);
  const bottomRef = useRef(null);

  const exhausted = exchangeCount >= MAX_ALEX_EXCHANGES;

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading || exhausted) return;
    const nextExchange = exchangeCount + 1;
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setLoading(true);
    try {
      const res = await api('/api/questions/ask-alex', {
        method: 'POST',
        body: JSON.stringify({ question_id: questionId, message: msg, exchange_number: nextExchange, was_correct: wasCorrect }),
      });
      const cleanText = stripMarkdown(res.response || '');
      const isLast = res.exchanges_remaining === 0;
      setMessages(prev => [...prev, { role: 'alex', text: isLast ? ALEX_LIMIT_MESSAGE : cleanText }]);
      setExchangeCount(nextExchange);
    } catch {
      setMessages(prev => [...prev, { role: 'alex', text: 'Something went wrong. Try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const openLabel = wasCorrect
    ? "Got it right? Alex can explain the reasoning if you're curious."
    : 'Ask Alex about this question';

  if (!open) {
    return (
      <button
        className={`alex-open-btn${wasCorrect ? ' alex-open-btn-correct' : ''}`}
        onClick={() => setOpen(true)}
      >
        {openLabel}
      </button>
    );
  }

  return (
    <div className={`alex-panel${wasCorrect ? ' alex-panel-correct' : ''}`}>
      <div className="alex-header">
        <span className="alex-title">Alex</span>
        <button className="alex-close-btn" onClick={() => setOpen(false)}>×</button>
      </div>
      {messages.length === 0 && (
        <div className="alex-intro">
          {wasCorrect
            ? 'Ask about the reasoning if anything was unclear.'
            : 'Ask a question about this problem. Alex can help you understand why.'}
        </div>
      )}
      <div className="alex-thread">
        {messages.map((m, i) => (
          <div key={i} className={`alex-msg alex-msg-${m.role}`}>
            {m.role === 'alex' && <span className="alex-msg-label">Alex</span>}
            <div className="alex-msg-text">{m.text}</div>
          </div>
        ))}
        {loading && (
          <div className="alex-msg alex-msg-alex">
            <span className="alex-msg-label">Alex</span>
            <div className="alex-msg-text">
              <span className="alex-typing">
                <span className="alex-dot" />
                <span className="alex-dot" />
                <span className="alex-dot" />
              </span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {!exhausted && (
        <div className="alex-input-row">
          <input
            className="alex-input"
            type="text"
            placeholder="Ask a question"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            disabled={loading}
          />
          <button className="alex-send-btn" onClick={handleSend} disabled={loading || !input.trim()}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

export default AskAlex;
